import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AdeProfile {
  id: string;
  profile_name: string;
  description: string | null;
  profile_uuid: string | null;
  customer_id: string | null;
  abm_connection_id: string;
  device_count: number | null;
  is_mandatory: boolean | null;
  is_mdm_removable: boolean | null;
  await_device_configured: boolean | null;
  auto_advance_setup: boolean | null;
  skip_setup_items: any | null;
  configuration_web_url: string | null;
  support_phone_number: string | null;
  support_email_address: string | null;
  department: string | null;
  anchor_certs: string[] | null;
  supervising_host_certs: string[] | null;
  org_magic: string | null;
  is_draft: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  abm_connection?: {
    name: string;
    customer_id: string | null;
    organization_id: string;
  };
}

export const useAdeProfiles = (customerId?: string) => {
  const [profiles, setProfiles] = useState<AdeProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchProfiles();
  }, [customerId]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('ade_profiles')
        .select(`
          *,
          abm_connection:abm_connections(
            name,
            customer_id,
            organization_id
          )
        `)
        .order('created_at', { ascending: false });

      // Filter by customer if provided
      if (customerId) {
        query = query.eq('abm_connection.customer_id', customerId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      // Transform data to ensure proper typing with customer_id and is_draft
      const transformedData = (data || []).map(profile => ({
        ...profile,
        customer_id: profile.customer_id || null,
        is_draft: profile.is_draft ?? false,
      }));

      setProfiles(transformedData);
    } catch (err) {
      console.error('Error fetching ADE profiles:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch enrollment profiles'));
      toast({
        title: 'Error',
        description: 'Failed to load enrollment profiles',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createProfile = async (profileData: {
    customer_id: string;
    profile_name: string;
    description?: string;
    abm_connection_id: string;
    is_mandatory?: boolean;
    is_mdm_removable?: boolean;
    await_device_configured?: boolean;
    auto_advance_setup?: boolean;
    skip_setup_items?: any;
    configuration_web_url?: string;
    support_phone_number?: string;
    support_email_address?: string;
    department?: string;
    is_draft?: boolean;
  }) => {
    try {
      const { data, error: insertError } = await supabase
        .from('ade_profiles')
        .insert([profileData])
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      toast({
        title: 'Success',
        description: 'Enrollment profile created successfully',
      });

      await fetchProfiles();
      return data;
    } catch (err) {
      console.error('Error creating ADE profile:', err);
      toast({
        title: 'Error',
        description: 'Failed to create enrollment profile',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const updateProfile = async (id: string, updates: Partial<AdeProfile>) => {
    try {
      const { error: updateError } = await supabase
        .from('ade_profiles')
        .update(updates)
        .eq('id', id);

      if (updateError) {
        throw updateError;
      }

      toast({
        title: 'Success',
        description: 'Enrollment profile updated successfully',
      });

      await fetchProfiles();
    } catch (err) {
      console.error('Error updating ADE profile:', err);
      toast({
        title: 'Error',
        description: 'Failed to update enrollment profile',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const deleteProfile = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('ade_profiles')
        .delete()
        .eq('id', id);

      if (deleteError) {
        throw deleteError;
      }

      toast({
        title: 'Success',
        description: 'Enrollment profile deleted successfully',
      });

      await fetchProfiles();
    } catch (err) {
      console.error('Error deleting ADE profile:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete enrollment profile',
        variant: 'destructive',
      });
      throw err;
    }
  };

  return {
    profiles,
    loading,
    error,
    fetchProfiles,
    createProfile,
    updateProfile,
    deleteProfile,
  };
};
